import { useState } from 'react';
import { Search, Filter, ScrollText, User, Package, FileText, Shield, Clock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuditLog, type AuditEntry } from '@/hooks/useSettings';
import { initials } from '@/lib/utils';

const ACTION_FILTERS = [
  { value: '',             label: 'All actions' },
  { value: 'user.create',  label: 'User created' },
  { value: 'user.update',  label: 'User updated' },
  { value: 'user.delete',  label: 'User deleted' },
  { value: 'asset.upload-url', label: 'Asset uploaded' },
  { value: 'asset.delete', label: 'Asset deleted' },
  { value: 'settings.update', label: 'Settings updated' },
];

const RESOURCE_ICONS = {
  user:     User,
  asset:    Package,
  document: FileText,
  default:  ScrollText,
};

export function AuditSettings() {
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useAuditLog({
    limit: 200,
    action: actionFilter || undefined,
  });

  const filtered = (data ?? []).filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      e.actorEmail.toLowerCase().includes(q) ||
      e.action.toLowerCase().includes(q) ||
      e.resourceType.toLowerCase().includes(q) ||
      (e.resourceId && e.resourceId.toLowerCase().includes(q))
    );
  });

  if (isLoading) return <LoaderScreen />;

  return (
    <div className="space-y-6">
      <div>
        <div className="kicker mb-2">Audit · Last 200 events</div>
        <h2 className="font-display text-2xl text-text mb-2">Activity Log</h2>
        <p className="text-sm text-text-muted max-w-2xl">
          Append-only record of every privileged action: user provisioning, role changes, settings updates,
          asset operations. Visible to owners only.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            placeholder="Search by email, action, or resource…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-56">
          <Select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            options={ACTION_FILTERS}
          />
        </div>
      </div>

      {/* Log */}
      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState
            icon={Filter}
            title="No matching events"
            description="Try clearing your filters or selecting a wider time range."
          />
        ) : (
          <ol className="divide-y divide-border">
            {filtered.map((e) => (
              <AuditRow key={e.id} entry={e} />
            ))}
          </ol>
        )}
      </Card>

      <div className="flex items-start gap-3 p-4 bg-bg-subtle border border-border rounded-sm">
        <Shield size={14} className="text-text-dim shrink-0 mt-0.5" />
        <p className="text-xs text-text-muted leading-relaxed">
          Audit entries are <span className="text-text">append-only</span> by Firestore rules — no role can modify or
          delete records once written. The log is the canonical record for compliance and incident review.
        </p>
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const Icon = RESOURCE_ICONS[entry.resourceType as keyof typeof RESOURCE_ICONS] ?? RESOURCE_ICONS.default;

  // Pretty-print action: "user.create" → "User · Create"
  const [resourceVerb, verb] = entry.action.split('.');
  const actionLabel = verb
    ? `${resourceVerb.charAt(0).toUpperCase() + resourceVerb.slice(1)} · ${verb}`
    : entry.action;

  return (
    <li className="px-6 py-4 hover:bg-bg-hover/30 transition-colors">
      <div className="flex items-start gap-4">
        <div className="shrink-0 h-9 w-9 rounded-sm bg-bg-subtle border border-border flex items-center justify-center">
          <Icon size={14} className="text-text-muted" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-sm text-text">
              {actionLabel}
            </span>
            {entry.resourceId && (
              <span className="text-xs text-brass-300 font-mono">
                #{entry.resourceId.slice(0, 12)}
              </span>
            )}
            <Badge tone={entry.actorRole === 'owner' ? 'brass' : 'neutral'}>
              {entry.actorRole}
            </Badge>
          </div>

          <div className="mt-1 flex items-center gap-2 text-xs text-text-dim">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-4 w-4 rounded-sm bg-walnut-700 flex items-center justify-center font-display text-[8px] text-brass-200">
                {initials(entry.actorEmail)}
              </span>
              {entry.actorEmail}
            </span>
            <span className="text-border-strong">·</span>
            <span className="inline-flex items-center gap-1 tabular">
              <Clock size={11} />
              {formatTimestamp(entry.timestamp)}
            </span>
          </div>

          {Object.keys(entry.metadata ?? {}).length > 0 && (
            <details className="mt-2">
              <summary className="text-2xs text-text-dim cursor-pointer hover:text-text-muted uppercase tracking-ultra">
                Metadata
              </summary>
              <pre className="mt-2 text-2xs font-mono text-text-muted bg-bg-subtle p-2 rounded-sm overflow-x-auto border border-border">
{JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </li>
  );
}

function formatTimestamp(ts: string) {
  try {
    const d = new Date(ts);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return ts;
  }
}
